/**
 * Project Name: Brainsie eCommerce v4
 * Component: Default List
 * Description: List component for infographics
 * Author: Brainsie Team
 * Date Created: October 29, 2025
 */

import React from "react";
import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";

export default function DefaultList({
  component,
  section,
  darkMode,
  theme,
  settings = {},
  renderCTAButtons,
}) {
  console.log("DefaultList rendering with component:", component);

  // Get component data using correct field mapping
  const primaryText = component.Primary_Text || "";
  const secondaryText = component.Secondary_Text || "";
  const listContent = component.List_Content || "";
  const descriptiveText = component.Descriptive_Text || "";
  const primaryCTA = component.Primary_CTA || "";
  const secondaryCTA = component.Secondary_CTA || "";

  // Enhanced component settings with comprehensive defaults
  const componentSettings = {
    size: "medium",
    variant: "contained",
    color: "primary",
    fullWidth: false,
    disabled: false,
    fontSize: "",
    fontWeight: "",
    textAlign: "left",
    backgroundColor: "",
    textColor: "",
    borderRadius: "",
    padding: "",
    margin: "",
    elevation: 0,
    customStyles: "",
    iconType: "check",
    showIcon: true,
    ...settings,
    ...(component.Component_Settings
      ? typeof component.Component_Settings === "string"
        ? JSON.parse(component.Component_Settings)
        : component.Component_Settings
      : {}),
  };

  // Helper function to get theme-aware text color
  const getTextColor = () => {
    if (componentSettings.textColor) {
      return componentSettings.textColor;
    }
    return darkMode ? theme.palette.common.white : theme.palette.text.primary;
  };

  // Enhanced list items parsing - prioritize List_Content as JSON array
  const parseListItems = () => {
    // 1. First priority: List_Content as JSON array
    if (listContent) {
      try {
        const parsed = JSON.parse(listContent);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.filter((item) => item && item.trim());
        }
      } catch (error) {
        console.warn("Failed to parse List_Content as JSON:", error);
        // Fallback: treat as single item or delimited string
        if (listContent.includes("~")) {
          return listContent
            .split("~")
            .map((item) => item.trim())
            .filter((item) => item);
        }
        return [listContent];
      }
    }

    // 2. Fallback to Descriptive_Text with ~ delimiter
    if (descriptiveText && descriptiveText.includes("~")) {
      return descriptiveText
        .split("~")
        .map((item) => item.trim())
        .filter((item) => item);
    }

    // 3. Last resort: use descriptiveText as single item
    if (descriptiveText) {
      return [descriptiveText];
    }

    return [];
  };

  const listItems = parseListItems();

  // Don't render if no list items
  if (!listItems.length) {
    return null;
  }

  // Get icon based on settings
  const getListIcon = () => {
    const iconType = componentSettings.iconType || "check";
    const iconColor = componentSettings.iconColor || theme.palette.primary.main;

    switch (iconType) {
      case "bullet":
        return (
          <FiberManualRecordIcon sx={{ color: iconColor, fontSize: "small" }} />
        );
      case "arrow":
        return <ArrowRightIcon sx={{ color: iconColor }} />;
      case "check":
      default:
        return <CheckCircleIcon sx={{ color: iconColor }} />;
    }
  };

  // Get comprehensive styling
  const getComponentStyles = () => ({
    mb: componentSettings.margin ? 0 : 2,
    padding: componentSettings.padding || 0,
    margin: componentSettings.margin || "0 0 16px 0",
    backgroundColor: componentSettings.backgroundColor || "transparent",
    borderRadius: componentSettings.borderRadius || "0px",
    textAlign: componentSettings.textAlign || "left",
    width: componentSettings.fullWidth ? "100%" : "auto",
    opacity: componentSettings.disabled ? 0.6 : 1,
    boxShadow:
      componentSettings.elevation > 0
        ? `0px ${componentSettings.elevation}px ${
            componentSettings.elevation * 2
          }px rgba(0,0,0,0.12)`
        : "none",
    ...(componentSettings.customStyles
      ? (() => {
          try {
            return typeof componentSettings.customStyles === "string"
              ? JSON.parse(componentSettings.customStyles)
              : componentSettings.customStyles;
          } catch {
            return {};
          }
        })()
      : {}),
  });

  const getHeadingSize = () => {
    if (componentSettings.fontSize) return componentSettings.fontSize;
    return componentSettings.size === "large"
      ? "1.3rem"
      : componentSettings.size === "small"
      ? "0.95rem"
      : "1.1rem";
  };

  const getItemSize = () => {
    if (componentSettings.fontSize) return componentSettings.fontSize;
    return componentSettings.size === "large"
      ? "1.1rem"
      : componentSettings.size === "small"
      ? "0.875rem"
      : "1rem";
  };

  return (
    <Box sx={getComponentStyles()}>
      {/* Primary Text (List Heading) */}
      {primaryText && primaryText !== "NA" && (
        <Typography
          variant="h6"
          component="h4"
          sx={{
            mb: 1.5,
            color: getTextColor(),
            fontWeight: componentSettings.fontWeight || 600,
            fontSize: getHeadingSize(),
            textAlign: componentSettings.textAlign || "left",
          }}
        >
          {primaryText}
        </Typography>
      )}

      {/* Secondary Text */}
      {secondaryText && secondaryText !== "NA" && (
        <Typography
          variant="body2"
          sx={{
            mb: 1,
            color: getTextColor(),
            fontSize: componentSettings.size === "small" ? "0.8rem" : "0.9rem",
            textAlign: componentSettings.textAlign || "left",
            opacity: 0.8,
          }}
        >
          {secondaryText}
        </Typography>
      )}

      {/* Descriptive Text (if separate from list) */}
      {descriptiveText && !listItems.includes(descriptiveText) && (
        <Typography
          variant="body1"
          sx={{
            mb: 2,
            color: getTextColor(),
            fontSize: getItemSize(),
            textAlign: componentSettings.textAlign || "left",
            lineHeight: 1.6,
          }}
        >
          {descriptiveText}
        </Typography>
      )}

      {/* Enhanced List with settings integration */}
      <List sx={{ padding: 0 }}>
        {listItems.map((item, index) => (
          <ListItem
            key={index}
            sx={{
              paddingLeft: 0,
              paddingY: 0.5,
              alignItems: "flex-start",
            }}
          >
            {componentSettings.showIcon !== false && (
              <ListItemIcon
                sx={{
                  minWidth: 32,
                  marginTop: "2px",
                  color:
                    componentSettings.color === "primary"
                      ? theme.palette.primary.main
                      : componentSettings.color === "secondary"
                      ? theme.palette.secondary.main
                      : theme.palette.primary.main,
                }}
              >
                {getListIcon()}
              </ListItemIcon>
            )}
            <ListItemText
              primary={item}
              primaryTypographyProps={{
                variant: "body1",
                sx: {
                  color: getTextColor(),
                  fontSize: getItemSize(),
                  lineHeight: 1.5,
                  fontWeight: componentSettings.fontWeight || "normal",
                  textAlign: componentSettings.textAlign || "left",
                },
              }}
            />
          </ListItem>
        ))}
      </List>

      {/* CTA Buttons */}
      {(primaryCTA || secondaryCTA) &&
        renderCTAButtons &&
        renderCTAButtons(primaryCTA, secondaryCTA)}
    </Box>
  );
}
